import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../../../Context/LanguageContext';
import { useTheme } from '../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../Styles/theme';

import InventoryCountInput from '../../../../Component/SettingComponent/CountInput';
import PriceInput from '../../../../Component/SettingComponent/PriceInput';
import TextInput from '../../../../Component/SettingComponent/TextInput';
import TotalModalWrapper from '../../../Share/Normal/TotalModalWrapper';
import useDragAndDrop from '../../../Share/Normal/useDragAndDrop';

import NonePicturePath from '../../../../image/WhiteNonePictureIcon.png';

export default function ProductAddModal(props) {
  const { data, onClickSaveButton, onClickExitButton } = props;
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [name, setName] = useState('');
  const [price, setPrice] = useState(0);
  const [inventory, setInventory] = useState(0);

  const [profile, setProfile] = useState('');
	const [imgBase64, setImgBase64] = useState();
  const [imgFile, setImgFile] = useState();
  const [isUpLoad, setIsUpLoad] = useState(false);
  const [imgLocalPath, setImgLocalPath] = useState('');

	const hiddenFileInput = useRef();

  // 데이터 수정인 경우 데이터 세팅 //
  useEffect(() => {
    if (data === undefined) return;

    setName(data.name);
    setPrice(data.price);
    setInventory(data.inventory);
    setProfile(data.picture);
  }, [data])

  // 저장하기 클릭시 //
  function onClickSave() {
    onClickSaveButton(name, price, inventory, profile);
  }

  // 사진 변경 클릭시 //
  const onClickProfile = (e) => {
    hiddenFileInput.current.click();
	};

  // 파일 처리 공통 로직 (input change / drag-and-drop 공용) //
  const processFile = (file) => {
    const reader = new FileReader();
    setImgLocalPath(file.name);

    reader.onloadend = () => {
      const base64 = reader.result;
      if (base64) {
        setProfile(base64.toString());
        setIsUpLoad(true);
      }
    };

    reader.readAsDataURL(file);
    setImgFile(file);
  };

  // 사진변경시 //
  const onChangeProfile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'jpg' && ext !== 'png' && ext !== 'jpeg') {
      alert(t('settingProduct.imageFormatError'));
      let parentObj = e.target.parentNode
      let node = parentObj.replaceChild(e.target.cloneNode(true), e.target);

      return false;
    }

    processFile(file);
  };

  // Drag & Drop hook //
  const { isDragOver, dragHandlers } = useDragAndDrop({
    onFileSelect: processFile,
    onInvalidFileType: () => alert(t('settingProduct.imageFormatError')),
  });

  return (
    <TotalModalWrapper>
      <Modal>
        <InnerWrapper>
          <TitleWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{t('settingProduct.addTitle')}</Text>
            <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>{t('settingProduct.addDescription')}</Text>
          </TitleWrapper>
          <ContentWrapper>
            {
              profile === '' ?
                <EmptyProfileBox $isDragOver={isDragOver} onClick={onClickProfile} {...dragHandlers}>
                  <NonePicture src={NonePicturePath}/>
                </EmptyProfileBox>
                :
                <ProfileBox $isDragOver={isDragOver} onClick={onClickProfile} {...dragHandlers}>
                  <Profile src={profile} />
                </ProfileBox>
            }
            <input 
              type="file"
              ref={hiddenFileInput}
              onChange={onChangeProfile}
              style={{ display: 'none' }} />
            <InputBoxWrapper>
              <InputBox>
                <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.subText}>{t('settingProduct.name')}</Text>
                <InputWrapper>
                  <TextInput value={name} setValue={setName} placeholder={t('settingProduct.namePlaceholder')} />
                </InputWrapper>
              </InputBox>
              <InputBox>
                <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.subText}>{t('settingMembership.priceSettings')}</Text>
                <InputWrapper>
                  <PriceInput value={price} setValue={setPrice} />
                </InputWrapper>
              </InputBox>
              <InputBox>
                <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.subText}>{t('settingProduct.inventoryManagement')}</Text>
                <InputWrapper>
                  <InventoryCountInput value={inventory} setValue={setInventory} unit={t('settingProduct.inventoryUnit')} />
                </InputWrapper>
              </InputBox>
            </InputBoxWrapper>
          </ContentWrapper>
          <SaveButtonWrapper>
            <ButtonWrapper>
              <ExitButton onClick={onClickExitButton}>{t('settingMembership.cancel')}</ExitButton>
              <DeleteButton onClick={onClickSave}>{t('settingMembership.save')}</DeleteButton>
            </ButtonWrapper>
          </SaveButtonWrapper>
        </InnerWrapper>
      </Modal>
    </TotalModalWrapper>
  )
}

const Modal = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 20px;

  box-sizing: border-box;
  width: 474px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  margin-top: 30px;

  width: 100%;
  height: 222px;

  gap: 20px;
`;

const ProfileBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 222px;
  height: 222px;

  border: 2px solid ${props => props.$isDragOver ? props.theme.primary : 'transparent'};
  border-radius: 10px;
  box-sizing: border-box;

  overflow: hidden;

  transition: border-color 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const Profile = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;
`;

const NonePicture = styled.img`
  width: 35.26%;
  height: 35.26%;
`;

const EmptyProfileBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  min-width: 222px;
  min-height: 222px;

  border: 2px solid ${props => props.$isDragOver ? props.theme.primary : 'transparent'};
  border-radius: 10px;
  box-sizing: border-box;
  opacity: 0.7;
  background-color: ${props => props.theme.line};

  transition: border-color 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const InputBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 192px;

  gap: 15px;
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  
  gap: 5px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const SaveButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: flex-end;

  margin-top: 20px;

  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 62px;
  height: 40px;

  border: 1px solid ${props => props.theme.buttonFill};
  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.gray};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.subKey};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.white};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;


const Text = styled.p`
	font-family: ${props => props.fontFamily};
	font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  padding: 0 0 0 0;

  &:hover {
    cursor: ${props => props.hover};
  }
`;
